import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { resolveLabel } from '@/lib/fieldLabel';
import { ToggleRow, ComputedColumnsSection, SMALL_TRIGGER, TINY_TRIGGER } from './sections';

export function TableConfig({ cfg, set, currentVisual, allFields, designerMode, reportTableSpanPages, setReportTableSpanPages, reportTableRepeatHeader, setReportTableRepeatHeader }) {
  return (
    <React.Fragment>
      <ToggleRow label="Striped Rows" checked={cfg.striped !== false} onChange={v => set('striped', v)} />
      <ToggleRow label="Compact" checked={cfg.compact || false} onChange={v => set('compact', v)} />
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Label className="text-[8px] text-muted-foreground">Table Font</Label>
          <Select value={cfg.fontFamily || 'default'} onValueChange={v => set('fontFamily', v)}>
            <SelectTrigger className={SMALL_TRIGGER} data-testid="table-font-family"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="arial">Arial</SelectItem>
              <SelectItem value="helvetica">Helvetica</SelectItem>
              <SelectItem value="times">Times New Roman</SelectItem>
              <SelectItem value="georgia">Georgia</SelectItem>
              <SelectItem value="verdana">Verdana</SelectItem>
              <SelectItem value="courier">Courier New</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-20">
          <Label className="text-[8px] text-muted-foreground">Font Size</Label>
          <Input type="number" min="7" max="24" step="1" value={cfg.fontSize || 12} onChange={e => set('fontSize', Math.max(7, Math.min(24, Number(e.target.value) || 12)))} className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5" data-testid="table-font-size" />
        </div>
      </div>
      <ToggleRow label="Show Row Numbers" checked={cfg.rowNumbers || false} onChange={v => set('rowNumbers', v)} />
      <ToggleRow label="Freeze Header" checked={cfg.freezeHeader !== false} onChange={v => set('freezeHeader', v)} />
      <ToggleRow label="Merge Repeated Cells" checked={cfg.mergeRepeatedCells || false} onChange={v => set('mergeRepeatedCells', v)} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Max Rows Displayed</Label>
        <Input value={cfg.maxRows || 200} onChange={e => set('maxRows', Number(e.target.value))} type="number" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-20" />
      </div>
      <ToggleRow label="Show Grand Total" checked={cfg.grandTotals || false} onChange={v => set('grandTotals', v)} />
      {cfg.grandTotals && (
        <div>
          <Label className="text-[8px] text-muted-foreground">Totals Position</Label>
          <Select value={cfg.totalsPosition || 'bottom'} onValueChange={v => set('totalsPosition', v)}>
            <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="top">Top (below header)</SelectItem><SelectItem value="bottom">Bottom (sticky)</SelectItem></SelectContent>
          </Select>
          <Label className="text-[8px] text-muted-foreground mt-1.5 block">Grand Total Label</Label>
          <Input value={cfg.grandTotalLabel ?? 'Grand Total'} onChange={e => set('grandTotalLabel', e.target.value)} placeholder="Grand Total" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5" data-testid="table-grand-total-label" />
        </div>
      )}
      <ToggleRow label="Show Subtotals" checked={cfg.subtotals || false} onChange={v => set('subtotals', v)} />

      <div className="border-t border-border/20 pt-1.5 mt-1.5">
        <p className="text-[8px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Row Grouping</p>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Group By</Label>
        <Select value={(cfg.groupBy || [])[0] || '_none_'} onValueChange={v => set('groupBy', v === '_none_' ? [] : [v])}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none_">None</SelectItem>
            {(currentVisual?.rowDims || []).map(d => (
              <SelectItem key={d} value={d}>{resolveLabel(allFields, d)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(cfg.groupBy || []).length > 0 && (
        <>
          <div>
            <Label className="text-[8px] text-muted-foreground">Nested Group By</Label>
            <Select value={(cfg.groupBy || [])[1] || '_none_'} onValueChange={v => { const first = (cfg.groupBy || [])[0]; set('groupBy', v === '_none_' ? [first] : [first, v]); }}>
              <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">None</SelectItem>
                {(currentVisual?.rowDims || []).filter(d => d !== (cfg.groupBy || [])[0]).map(d => (
                  <SelectItem key={d} value={d}>{resolveLabel(allFields, d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[8px] text-muted-foreground">Group Footer Aggregation</Label>
            <Select value={cfg.groupAggregation || 'sum'} onValueChange={v => set('groupAggregation', v)}>
              <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sum">Sum</SelectItem>
                <SelectItem value="avg">Average</SelectItem>
                <SelectItem value="count">Count</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ToggleRow label="Show Group Footer" checked={cfg.groupFooter !== false} onChange={v => set('groupFooter', v)} />
          <ToggleRow label="Page Break per Group" checked={cfg.groupPageBreak || false} onChange={v => set('groupPageBreak', v)} />
          <div>
            <Label className="text-[8px] text-muted-foreground">Subtotal Label</Label>
            <Input value={cfg.subtotalLabel ?? 'Subtotal'} onChange={e => set('subtotalLabel', e.target.value)} placeholder="Subtotal — use {group} for the group name" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5" data-testid="table-subtotal-label" />
            <p className="text-[7px] text-muted-foreground/60 mt-0.5">Tip: include <span className="font-mono">{'{group}'}</span> to show the group name (e.g. "{'{group}'} total").</p>
          </div>
        </>
      )}

      <ComputedColumnsSection cfg={cfg} set={set} />

      <div className="border-t border-border/20 pt-1.5 mt-1.5">
        <p className="text-[8px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Conditional Formatting</p>
        {(currentVisual?.valueMeasures || []).map(m => {
          const mRules = (cfg.conditionalFormatting || {})[m] || [];
          const fieldLabel = resolveLabel(allFields, m);
          return (
            <div key={m} className="mb-2">
              <div className="flex items-center justify-between">
                <Label className="text-[9px] text-muted-foreground truncate">{fieldLabel}</Label>
                <Select value={mRules[0]?.type || '_none_'} onValueChange={v => {
                  if (v === '_none_') { const cf = { ...(cfg.conditionalFormatting || {}) }; delete cf[m]; set('conditionalFormatting', cf); }
                  else { set('conditionalFormatting', { ...(cfg.conditionalFormatting || {}), [m]: [{ type: v, ...(v === 'threshold' ? { operator: 'gt', value: 0, bgColor: '', textColor: '#ef4444', bold: false } : v === 'color_scale' ? { colors: [[76,175,80],[255,235,59],[244,67,54]] } : v === 'data_bar' ? { positiveColor: 'rgba(59,130,246,0.3)', negativeColor: 'rgba(239,68,68,0.3)' } : { thresholds: [33, 66], icons: ['▼', '●', '▲'], iconColors: ['#f44336', '#ff9800', '#4caf50'] }) }] }); }
                }}>
                  <SelectTrigger className={`${TINY_TRIGGER} w-24`}><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">None</SelectItem>
                    <SelectItem value="threshold">Threshold</SelectItem>
                    <SelectItem value="color_scale">Color Scale</SelectItem>
                    <SelectItem value="data_bar">Data Bar</SelectItem>
                    <SelectItem value="icon_set">Icon Set</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {mRules[0]?.type === 'threshold' && (
                <div className="flex gap-1 mt-1 items-center">
                  <Select value={mRules[0].operator || 'gt'} onValueChange={v => { const r = [...mRules]; r[0] = { ...r[0], operator: v }; set('conditionalFormatting', { ...(cfg.conditionalFormatting || {}), [m]: r }); }}>
                    <SelectTrigger className={`${TINY_TRIGGER} w-12`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="gt">&gt;</SelectItem><SelectItem value="lt">&lt;</SelectItem><SelectItem value="gte">&ge;</SelectItem><SelectItem value="lte">&le;</SelectItem></SelectContent>
                  </Select>
                  <Input type="number" value={mRules[0].value || 0} onChange={e => { const r = [...mRules]; r[0] = { ...r[0], value: Number(e.target.value) }; set('conditionalFormatting', { ...(cfg.conditionalFormatting || {}), [m]: r }); }} className="!h-5 !text-[8px] !rounded !bg-muted/30 !border-border/30 w-16" />
                  <input type="color" value={mRules[0].textColor || '#ef4444'} onChange={e => { const r = [...mRules]; r[0] = { ...r[0], textColor: e.target.value }; set('conditionalFormatting', { ...(cfg.conditionalFormatting || {}), [m]: r }); }} className="w-5 h-5 rounded cursor-pointer" />
                </div>
              )}
            </div>
          );
        })}
        {(currentVisual?.valueMeasures || []).length === 0 && <p className="text-[8px] text-muted-foreground/70 italic">Add measures to configure formatting</p>}
      </div>
      {designerMode === 'report' && (
        <>
          <div className="border-t border-border/20 pt-1.5 mt-1.5">
            <p className="text-[8px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Report Page Options</p>
          </div>
          <ToggleRow label="Tables span multiple pages" checked={reportTableSpanPages || false} onChange={v => setReportTableSpanPages(v)} />
          <ToggleRow label="Repeat header on each page" checked={reportTableRepeatHeader || false} onChange={v => setReportTableRepeatHeader(v)} />
        </>
      )}
    </React.Fragment>
  );
}

export function PivotConfig({ cfg, set, currentVisual, allFields }) {
  return (
    <React.Fragment>
      <ToggleRow label="Show Grand Totals" checked={cfg.grandTotals !== false} onChange={v => set('grandTotals', v)} />
      <ToggleRow label="Show Subtotals" checked={cfg.subtotals || false} onChange={v => set('subtotals', v)} />
      <ToggleRow label="Compact Layout" checked={cfg.compact || false} onChange={v => set('compact', v)} />
      {(currentVisual?.valueMeasures?.length > 0 || currentVisual?.rowDims?.length > 0) && (
        <div className="border-t border-border/20 pt-2 mt-1 space-y-1.5">
          <p className="text-[8px] text-muted-foreground uppercase tracking-wider font-semibold">Column Settings</p>
          <div className="max-h-32 overflow-auto space-y-1">
            {[...(currentVisual?.rowDims || []), ...(currentVisual?.valueMeasures || [])].map(col => {
              const colLabel = resolveLabel(allFields, col);
              const align = (cfg.columnAlignments || {})[col] || 'auto';
              const width = (cfg.columnWidths || {})[col] || '';
              return (
                <div key={col} className="flex items-center gap-1 text-[9px]">
                  <span className="truncate flex-1 text-muted-foreground" title={col}>{colLabel}</span>
                  <Select value={align} onValueChange={v => set('columnAlignments', { ...(cfg.columnAlignments || {}), [col]: v })}>
                    <SelectTrigger className={`${TINY_TRIGGER} !w-14 !bg-muted/20 !border-border/20`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="auto">Auto</SelectItem><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                  </Select>
                  <Input type="number" value={width} onChange={e => set('columnWidths', { ...(cfg.columnWidths || {}), [col]: e.target.value ? Number(e.target.value) : undefined })} placeholder="W" className="!h-5 !w-12 !text-[8px] !rounded !bg-muted/20 !border-border/20 text-foreground" title="Width (px)" />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export function LossTriangleConfig({ cfg, set, currentVisual, allFields }) {
  return (
    <React.Fragment>
      <ToggleRow label="Compact" checked={cfg.compact || false} onChange={v => set('compact', v)} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Origin Field</Label>
        <Select value={(cfg.triangleConfig || {}).originField || '_auto_'} onValueChange={v => set('triangleConfig', { ...(cfg.triangleConfig || {}), originField: v === '_auto_' ? undefined : v })}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_auto_">Auto (first dimension)</SelectItem>
            {(currentVisual?.rowDims || []).map(d => <SelectItem key={d} value={d}>{resolveLabel(allFields, d)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Development Period Field</Label>
        <Select value={(cfg.triangleConfig || {}).devField || '_auto_'} onValueChange={v => set('triangleConfig', { ...(cfg.triangleConfig || {}), devField: v === '_auto_' ? undefined : v })}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_auto_">Auto (second dimension)</SelectItem>
            {(currentVisual?.rowDims || []).map(d => <SelectItem key={d} value={d}>{resolveLabel(allFields, d)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Tail Factor</Label>
        <Input type="number" step="0.001" min="1" value={(cfg.triangleConfig || {}).tailFactor || 1.0} onChange={e => set('triangleConfig', { ...(cfg.triangleConfig || {}), tailFactor: Number(e.target.value) || 1.0 })} className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5 w-20" />
      </div>
      <div>
        <Label className="text-[8px] text-muted-foreground">Projection Method</Label>
        <Select value={(cfg.triangleConfig || {}).projectionMethod || 'chain_ladder'} onValueChange={v => set('triangleConfig', { ...(cfg.triangleConfig || {}), projectionMethod: v })}>
          <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="chain_ladder">Chain Ladder</SelectItem>
            <SelectItem value="bornhuetter_ferguson">Bornhuetter-Ferguson</SelectItem>
            <SelectItem value="cape_cod">Cape Cod</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </React.Fragment>
  );
}
